<template>
  <div class="min-h-screen py-8">
    <Head title="Gastos de Condominio" />

    <!-- Top Header -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between">
        <div class="flex-1 min-w-0">
          <h1 class="text-3xl font-extrabold text-gray-900 sm:truncate">Gastos de Condominio</h1>
          <p class="mt-1 text-sm text-gray-500">
            Visualización y control de egresos de la junta correspondientes a {{ getMonthName(form.month) }} del {{ form.year }}.
          </p>
        </div>
        <div class="mt-4 flex flex-wrap md:mt-0 md:ml-4 gap-2">
          <Link
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            href="/spent-categories"
          >
            <svg class="-ml-1 mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            Categorías
          </Link>
          <a
            target="_blank"
            :href="`/spentsReports/pdf?month=${form.month}&year=${form.year}`"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Exportar PDF
          </a>
          <Link
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            href="/spents/create"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Registrar Gasto
          </Link>
          <Link
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            :href="`/spents/generateOrders?month=${form.month}&year=${form.year}`"
            as="button"
            type="button"
            method="get"
            @click="confirmAlicuotaGeneration($event)"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Generar Alícuota
          </Link>
        </div>
      </div>
    </div>

    <!-- Dashboard KPI Cards -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <!-- Card 1: Total Gastos -->
        <div class="bg-indigo-600 overflow-hidden shadow rounded-lg text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white bg-opacity-20 rounded-md p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-indigo-100 truncate">Total Gastos del Mes</dt>
                  <dd class="text-2xl font-bold">Bs. {{ totalMonth.toFixed(2) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 2: Alicuota Calculada -->
        <div class="bg-green-600 overflow-hidden shadow rounded-lg text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white bg-opacity-20 rounded-md p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-green-100 truncate">Alícuota por Propietario</dt>
                  <dd class="text-2xl font-bold">Bs. {{ (totalMonth / (clientCount || 1)).toFixed(2) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 3: Propietarios Activos -->
        <div class="bg-blue-600 overflow-hidden shadow rounded-lg text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white bg-opacity-20 rounded-md p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-blue-100 truncate">Propietarios Activos</dt>
                  <dd class="text-2xl font-bold">{{ clientCount }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Row -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
      <div class="bg-white p-6 rounded-lg shadow-sm flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
        <!-- Search Input -->
        <div class="flex-1">
          <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">Buscar Gasto</label>
          <div class="relative">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              v-model="form.search"
              placeholder="Ej: Mantenimiento bomba..."
              class="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        <!-- Month Filter -->
        <div class="w-full md:w-48">
          <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">Mes</label>
          <select
            v-model="form.month"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option v-for="m in months" :key="m.value" :value="m.value">{{ m.name }}</option>
          </select>
        </div>

        <!-- Year Filter -->
        <div class="w-full md:w-36">
          <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">Año</label>
          <select
            v-model="form.year"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>

        <!-- Reset Button -->
        <div class="pt-5 flex items-end">
          <button
            @click="reset"
            class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>
    </div>

    <!-- Main Layout Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <!-- Spents Table (Col-span 8) -->
        <div class="lg:col-span-8">
          <div class="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-150">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Categoría</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Método</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Monto</th>
                    <th scope="col" class="relative px-6 py-3">
                      <span class="sr-only">Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="spent in spents.data" :key="spent.id" class="hover:bg-gray-50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-semibold text-gray-900">{{ spent.name }}</div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-50 text-indigo-700">
                        {{ spent.category?.name }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatDate(spent.date) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {{ spent.method }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      Bs. {{ spent.amount.toFixed(2) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link :href="`/spents/${spent.id}/edit`" class="text-indigo-600 hover:text-indigo-900">
                        Editar
                      </Link>
                    </td>
                  </tr>
                  <tr v-if="spents.data.length === 0">
                    <td colspan="6" class="px-6 py-8 text-center text-sm text-gray-500">
                      No se encontraron gastos registrados para el mes seleccionado.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- Pagination -->
          <div class="mt-6">
            <pagination :links="spents.meta.links" />
          </div>
        </div>

        <!-- Category Summary & Alicuota Breakdown (Col-span 4) -->
        <div class="lg:col-span-4 space-y-6">
          <!-- Category Breakdown Card -->
          <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-150">
            <h2 class="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Distribución de Gastos</h2>
            <div v-if="summary.length > 0" class="space-y-4">
              <div v-for="item in summary" :key="item.spent_category_id">
                <div class="flex items-center justify-between text-sm mb-1">
                  <span class="font-medium text-gray-700">{{ item.category?.name }}</span>
                  <span class="font-semibold text-gray-900">Bs. {{ item.total.toFixed(2) }} ({{ getPercentage(item.total) }}%)</span>
                </div>
                <div class="w-full bg-gray-100 rounded-full h-2">
                  <div
                    class="bg-indigo-600 h-2 rounded-full"
                    :style="{ width: getPercentage(item.total) + '%' }"
                  ></div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-6 text-sm text-gray-400">
              No hay resumen de distribución disponible.
            </div>
          </div>

          <!-- Alicuota Information Card -->
          <div class="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg shadow-sm border border-gray-150">
            <h2 class="text-lg font-bold text-gray-900 mb-2">Acerca de la Alícuota</h2>
            <p class="text-sm text-gray-600 mb-4 leading-relaxed">
              La alícuota se calcula sumando todos los egresos del condominio registrados durante el mes y dividiéndola equitativamente entre los propietarios activos.
            </p>
            <div class="rounded-md bg-indigo-50 p-4 border border-indigo-100">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-semibold text-indigo-800">Cálculo de este periodo</h3>
                  <div class="mt-1 text-sm text-indigo-700">
                    <p>Bs. {{ totalMonth.toFixed(2) }} / {{ clientCount }} Propietarios = **Bs. {{ (totalMonth / (clientCount || 1)).toFixed(2) }}** por parcela.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    spents: Object,
    summary: Array,
    totalMonth: Number,
    clientCount: Number,
  },
  data() {
    return {
      form: {
        search: this.filters.search || '',
        month: this.filters.month || new Date().getMonth() + 1,
        year: this.filters.year || new Date().getFullYear(),
      },
      months: [
        { value: 1, name: 'Enero' },
        { value: 2, name: 'Febrero' },
        { value: 3, name: 'Marzo' },
        { value: 4, name: 'Abril' },
        { value: 5, name: 'Mayo' },
        { value: 6, name: 'Junio' },
        { value: 7, name: 'Julio' },
        { value: 8, name: 'Agosto' },
        { value: 9, name: 'Septiembre' },
        { value: 10, name: 'Octubre' },
        { value: 11, name: 'Noviembre' },
        { value: 12, name: 'Diciembre' },
      ],
      years: Array.from({ length: 11 }, (_, i) => 2016 + i), // 2016 to 2026
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/spents', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = {
        search: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
      }
    },
    getMonthName(monthVal) {
      const match = this.months.find(m => m.value === (int => int)(monthVal));
      if (!match) {
        // Fallback for types
        const numeric = parseInt(monthVal, 10);
        const secondMatch = this.months.find(m => m.value === numeric);
        return secondMatch ? secondMatch.name : 'Mes';
      }
      return match.name;
    },
    getPercentage(categoryAmount) {
      if (this.totalMonth === 0) return '0.0';
      return ((categoryAmount / this.totalMonth) * 100).toFixed(1);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
      } catch (e) {
        return dateString;
      }
    },
    confirmAlicuotaGeneration(event) {
      const monthName = this.getMonthName(this.form.month);
      const message = `¿Está seguro de que desea generar los avisos de cobro y asignar la alícuota de $${(this.totalMonth / (this.clientCount || 1)).toFixed(2)} a los ${this.clientCount} propietarios correspondientes a ${monthName} de ${this.form.year}?`;
      if (!confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  },
}
</script>